import _extends from "@babel/runtime/helpers/esm/extends";
import { warn } from '@base-ui/utils/warn';
import { EMPTY_OBJECT } from '@base-ui/utils/empty';
import { DEFAULT_SCHEDULER_PREFERENCES, SchedulerStore } from "../internals/utils/SchedulerStore/index.mjs";
import { createChangeEventDetails } from "../base-ui-copy/utils/createBaseUIEventDetails.mjs";
export const DEFAULT_VIEWS = ['week', 'day', 'month', 'agenda'];
export const DEFAULT_VIEW = 'week';
export const DEFAULT_EVENT_CALENDAR_PREFERENCES = _extends({}, DEFAULT_SCHEDULER_PREFERENCES, {
  showWeekends: true,
  showWeekNumber: false,
  showEmptyDaysInAgenda: true,
  isSidePanelOpen: true
});
export const DEFAULT_PREFERENCES_MENU_CONFIG = {
  toggleWeekendVisibility: true,
  toggleWeekNumberVisibility: true,
  toggleEmptyDaysInAgenda: true,
  toggleAmpm: true
};
const deriveStateFromParameters = parameters => ({
  views: parameters.views ?? DEFAULT_VIEWS
});
const mapper = {
  getInitialState: (schedulerInitialState, parameters) => _extends({}, schedulerInitialState, deriveStateFromParameters(parameters), {
    preferences: parameters.preferences ?? parameters.defaultPreferences ?? EMPTY_OBJECT,
    preferencesMenuConfig: parameters.preferencesMenuConfig === false ? parameters.preferencesMenuConfig : _extends({}, DEFAULT_PREFERENCES_MENU_CONFIG, parameters.preferencesMenuConfig),
    viewConfig: null,
    view: parameters.view ?? parameters.defaultView ?? DEFAULT_VIEW
  }),
  updateStateFromParameters: (newSchedulerState, parameters, updateModel) => {
    const newState = _extends({}, newSchedulerState, deriveStateFromParameters(parameters));
    updateModel(newState, 'view', 'defaultView');
    updateModel(newState, 'preferences', 'defaultPreferences');
    return newState;
  }
};

/**
 * Base class that can be extended by premium stores.
 * Accepts instanceName as a parameter to allow subclasses to provide their own instance name.
 */
export class ExtendableEventCalendarStore extends SchedulerStore {
  constructor(parameters, adapter, instanceName) {
    super(parameters, adapter, instanceName, mapper);
    if (process.env.NODE_ENV !== 'production') {
      // Add listeners to assert the state validity (not applied in prod)
      this.subscribe(state => {
        this.assertViewValidity(state.view);
        return null;
      });
    }
  }
  assertViewValidity(view) {
    const views = this.state.views;
    if (!views.includes(view)) {
      throw /* FIXME (minify-errors-in-prod): Unminifyable error in production! */new Error([`MUI: The component tried to switch to the "${view}" view but it is not compatible with the available views: ${views.join(', ')}.`, 'Please ensure that the requested view is included in the views array.'].join('\n'));
    }
  }
  setVisibleDateAndView = (visibleDate, view, event) => {
    const {
      visibleDate: visibleDateProp,
      view: viewProp,
      onVisibleDateChange,
      onViewChange
    } = this.parameters;
    const hasVisibleDateChange = visibleDate !== this.state.visibleDate;
    const hasViewChange = view !== this.state.view;
    if (!hasVisibleDateChange && !hasViewChange) {
      return;
    }
    this.assertViewValidity(view);
    const eventDetails = createChangeEventDetails('none', event.nativeEvent);
    if (hasVisibleDateChange) {
      onVisibleDateChange?.(visibleDate, eventDetails);
    }
    if (hasViewChange) {
      onViewChange?.(view, eventDetails);
    }
    if (eventDetails.isCanceled) {
      return;
    }
    const canSetVisibleDate = visibleDateProp === undefined && hasVisibleDateChange;
    const canSetView = viewProp === undefined && hasViewChange;
    if (canSetVisibleDate || canSetView) {
      this.update(_extends({}, canSetVisibleDate ? {
        visibleDate
      } : undefined, canSetView ? {
        view
      } : undefined));
    }
  };
  setSiblingVisibleDate = (delta, event) => {
    const siblingVisibleDateGetter = this.state.viewConfig?.siblingVisibleDateGetter;
    if (!siblingVisibleDateGetter) {
      warn('MUI: No config found for the current view. Please use useInitializeView in your custom view.');
      return;
    }
    this.setVisibleDate({
      visibleDate: siblingVisibleDateGetter({
        delta,
        state: this.state
      }),
      event
    });
  };

  /**
   * Sets the view of the calendar.
   */
  setView = (view, event) => {
    const {
      view: viewProp,
      onViewChange
    } = this.parameters;
    if (view !== this.state.view) {
      this.assertViewValidity(view);
      const eventDetails = createChangeEventDetails('none', event);
      onViewChange?.(view, eventDetails);
      if (!eventDetails.isCanceled && viewProp === undefined) {
        this.set('view', view);
      }
    }
  };

  /**
   * Goes to the previous visible date span based on the current view.
   */
  goToPreviousVisibleDate = event => this.setSiblingVisibleDate(-1, event);

  /**
   * Goes to the next visible date span based on the current view.
   */
  goToNextVisibleDate = event => this.setSiblingVisibleDate(1, event);

  /**
   * Goes to a specific day and set the view to 'day'.
   */
  switchToDay = (visibleDate, event) => {
    this.setVisibleDateAndView(visibleDate, 'day', event);
  };

  /**
   * Updates some preferences of the calendar.
   */
  setPreferences = (partialPreferences, event) => {
    const {
      preferences: preferencesProp,
      onPreferencesChange
    } = this.parameters;
    const updated = _extends({}, this.state.preferences, partialPreferences);
    const eventDetails = createChangeEventDetails('none', event);
    onPreferencesChange?.(updated, eventDetails);
    if (!eventDetails.isCanceled && preferencesProp === undefined) {
      this.set('preferences', updated);
    }
  };

  /**
   * Sets the method used to determine the previous / next visible date.
   * Returns the cleanup function.
   */
  setViewConfig = config => {
    this.set('viewConfig', config);
    return () => this.set('viewConfig', null);
  };
  buildPublicAPI() {
    return _extends({}, super.buildPublicAPI());
  }
}

/**
 * Store for the EventCalendar component.
 */
export class EventCalendarStore extends ExtendableEventCalendarStore {
  constructor(parameters, adapter) {
    super(parameters, adapter, 'EventCalendarStore');
  }
}